import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import type { KanbanList, KanbanTag } from "../../../types/kanban";

/**
 * Per-channel kanban + channel-tag view state (step 5.2a; tags reassigned here
 * from 5.1c — they're co-loaded from `/kanban`). `kanbanByChan` and
 * `channelTagsByChan` are keyed by fully-qualified channel id. No inputs — the
 * loaders take an fqChanId and use module imports + internal state.
 *
 * All states + setters + loaders are returned. Stays in the composition layer
 * and reads the returned maps/loaders: the `currentKanban*`/`currentChannelTags`
 * derivations, `kanbanReloadedRef` + the retry-empty effect, the favorites
 * kanban-preload effects, and the navigation/onChannelList loader callers.
 */
export function useKanbanData() {
  const [kanbanByChan, setKanbanByChan] = useState<Record<string, KanbanList[]>>({});
  const [channelTagsByChan, setChannelTagsByChan] = useState<Record<string, KanbanTag[]>>({});
  const channelTagsRef = useRef(channelTagsByChan);
  useEffect(() => { channelTagsRef.current = channelTagsByChan; }, [channelTagsByChan]);

  const fetchChannelTags = useCallback(async (fqChanId: string, options?: { force?: boolean }) => {
    if (!fqChanId) return;
    const alreadyLoaded = Object.prototype.hasOwnProperty.call(channelTagsRef.current, fqChanId);
    if (alreadyLoaded && !options?.force) return;
    try {
      const tok = getAuthToken() || '';
      const res = await api.getAuth(`/channel-tags?channelId=${encodeURIComponent(fqChanId)}`, tok);
      const tags = Array.isArray(res?.tags) ? (res.tags as KanbanTag[]) : [];
      setChannelTagsByChan(old => ({ ...old, [fqChanId]: tags }));
    } catch {
      setChannelTagsByChan(old => (Object.prototype.hasOwnProperty.call(old, fqChanId) ? old : { ...old, [fqChanId]: [] }));
    }
  }, []);

  async function loadKanbanIfNeeded(fqChanId: string) {
    try {
      if (Object.prototype.hasOwnProperty.call(kanbanByChan, fqChanId)) return;
      const tok = getAuthToken() || '';
      const res = await api.getAuth(`/kanban?channelId=${encodeURIComponent(fqChanId)}`, tok);
      const lists = Array.isArray(res?.lists) ? (res.lists as KanbanList[]) : [];
      const tags = Array.isArray(res?.tags) ? (res.tags as KanbanTag[]) : [];
      setKanbanByChan(old => ({ ...old, [fqChanId]: lists }));
      setChannelTagsByChan(old => ({ ...old, [fqChanId]: tags }));
    } catch {
      // Mark as loaded (empty) to avoid endless "Loading" and allow UI to render fallback
      setKanbanByChan(old => (Object.prototype.hasOwnProperty.call(old, fqChanId) ? old : { ...old, [fqChanId]: [] }));
      setChannelTagsByChan(old => (Object.prototype.hasOwnProperty.call(old, fqChanId) ? old : { ...old, [fqChanId]: [] }));
    }
  }

  async function reloadKanban(fqChanId: string) {
    try {
        const tok = getAuthToken() || '';
      const res = await api.getAuth(`/kanban?channelId=${encodeURIComponent(fqChanId)}`, tok);
      const lists = Array.isArray(res?.lists) ? (res.lists as KanbanList[]) : [];
      const tags = Array.isArray(res?.tags) ? (res.tags as KanbanTag[]) : [];
      setKanbanByChan(old => ({ ...old, [fqChanId]: lists }));
      setChannelTagsByChan(old => ({ ...old, [fqChanId]: tags }));
    } catch {
      // keep existing state on error
    }
  }
  const mutateKanbanLists = useCallback((fqChanId: string, updater: (prev: KanbanList[]) => KanbanList[]) => {
    setKanbanByChan(old => {
      const prev = old[fqChanId] || [];
      const next = updater(prev);
      if (prev === next) return old;
      return { ...old, [fqChanId]: next };
    });
  }, []);
  const mutateChannelTags = useCallback((fqChanId: string, updater: (prev: KanbanTag[]) => KanbanTag[]) => {
    setChannelTagsByChan(old => {
      const prev = old[fqChanId] || [];
      const next = updater(prev);
      if (prev === next) return old;
      return { ...old, [fqChanId]: next };
    });
  }, []);

  return {
    kanbanByChan,
    setKanbanByChan,
    channelTagsByChan,
    setChannelTagsByChan,
    fetchChannelTags,
    loadKanbanIfNeeded,
    reloadKanban,
    mutateKanbanLists,
    mutateChannelTags,
  };
}
